import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2, Mail, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ phase: "loading", email: null });

  useEffect(() => {
    if (!token) {
      setState({ phase: "invalid", email: null });
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/auth/email/verify?token=${encodeURIComponent(token)}`);
        const data = await res.json();
        setState({ phase: data.status || "invalid", email: data.email || null });
      } catch (e) {
        setState({ phase: "invalid", email: null });
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6 py-12" data-testid="verify-email-page">
      <div className="w-full max-w-md bg-white border border-neutral-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="bg-slate-900 text-white px-6 py-5 flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center">
            <Star className="w-5 h-5 text-amber-400 fill-amber-400" strokeWidth={1.5} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-base">AdmissionCrafts</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate-300">Email verification</div>
          </div>
        </div>
        <div className="p-8">
          {state.phase === "loading" && (
            <div className="text-center" data-testid="verify-loading">
              <Loader2 className="w-10 h-10 mx-auto text-slate-400 animate-spin" />
              <p className="mt-4 text-sm text-slate-600">Verifying your email…</p>
            </div>
          )}
          {state.phase === "ok" && (
            <div className="text-center" data-testid="verify-ok">
              <div className="w-14 h-14 rounded-full bg-emerald-50 mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="mt-5 font-display text-2xl font-bold text-slate-900">Email verified</h1>
              <p className="mt-2 text-sm text-slate-600">
                {state.email ? <><span className="font-semibold">{state.email}</span> is confirmed.</> : "Your email is confirmed."}
                <br />You can now use all features of AdmissionCrafts.
              </p>
              <Link
                to="/dashboard"
                className="mt-6 inline-flex items-center justify-center h-11 px-5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition-colors"
                data-testid="verify-ok-continue"
              >
                Continue to dashboard
              </Link>
            </div>
          )}
          {state.phase === "already_verified" && (
            <div className="text-center" data-testid="verify-already">
              <div className="w-14 h-14 rounded-full bg-emerald-50 mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="mt-5 font-display text-2xl font-bold text-slate-900">Already verified</h1>
              <p className="mt-2 text-sm text-slate-600">
                {state.email ? <><span className="font-semibold">{state.email}</span> was previously confirmed.</> : "This email was already verified."}
              </p>
              <Link
                to="/dashboard"
                className="mt-6 inline-flex items-center justify-center h-11 px-5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition-colors"
                data-testid="verify-already-continue"
              >
                Continue to dashboard
              </Link>
            </div>
          )}
          {state.phase === "expired" && (
            <Failure
              testid="verify-expired"
              title="Verification link expired"
              body="This link is older than 24 hours. Sign in and request a new one from your dashboard."
            />
          )}
          {state.phase === "invalid" && (
            <Failure
              testid="verify-invalid"
              title="Invalid verification link"
              body="We couldn't recognise this link. It may have been used already, or copied incompletely. Sign in and request a fresh verification email."
            />
          )}
        </div>
      </div>
    </div>
  );
}

function Failure({ title, body, testid }) {
  return (
    <div className="text-center" data-testid={testid}>
      <div className="w-14 h-14 rounded-full bg-amber-50 mx-auto flex items-center justify-center">
        <AlertTriangle className="w-8 h-8 text-amber-600" />
      </div>
      <h1 className="mt-5 font-display text-2xl font-bold text-slate-900">{title}</h1>
      <p className="mt-2 text-sm text-slate-600">{body}</p>
      <Link
        to="/"
        className="mt-6 inline-flex items-center justify-center h-11 px-5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition-colors gap-2"
        data-testid="verify-fail-continue"
      >
        <Mail className="w-4 h-4" /> Go to sign in
      </Link>
    </div>
  );
}
